// The sidebar, defined in code and grouped by the work staff do.
//
// Who sees what is still decided by each role's permissions, which are the
// names of the old database menu entries (Users > Roles). Each item lists the
// old names that used to lead to the same page; a user sees the item when
// they hold any of them. Holding a child name is enough — the old menu also
// required its parent module, so agents could not reach "RTB Forms" at all.
// Items with no names are for administrators only.

export const NAV = [
    {
        label: null,
        items: [
            {label: 'Home', to: '/', always: true},
        ],
    },
    {
        label: 'Tenancies',
        items: [
            {label: 'Units & leases', to: '/buildium/rental-units', perms: ['RTB 30-10 Days Notice', 'Rental Propertis', 'Buildium', 'DataLink']},
            {label: 'Properties', to: '/buildium/rental-properties', perms: ['Rental Propertis', 'Buildium', 'DataLink']},
            // Rental and association owners: one entry, tabs on the page.
            {label: 'Owners', to: '/buildium/rental-owners', also: ['/buildium/association-owners'], perms: ['Rental Owners', 'Aassociation Owners', 'Buildium', 'DataLink']},
            {label: 'Associations', to: '/buildium/associations', perms: ['Associations', 'Buildium', 'DataLink']},
        ],
    },
    {
        label: 'Notices & forms',
        items: [
            {label: 'Rent increase notices', to: '/forms?type=rtb7', perms: ['RTB 7-Rent Increase', 'RTB Forms', 'OpsBoard']},
            {label: 'Create rent increases', to: '/buildium/lease-rents', perms: ['RTB 7-Rent Increase', 'OpsBoard']},
            {label: 'Monthly run & settings', to: '/rent-increase'},
            {label: 'Park site figures (RTB-11a)', to: '/rent-increase/parks'},
            {label: 'End of tenancy notices', to: '/forms?type=rtb30', perms: ['RTB 30-10 Days Notice', 'RTB Forms', 'OpsBoard']},
            {label: 'Inspection reports', to: '/inspection-reports', perms: ['sterling.inspection_reports', 'RTB Forms', 'OpsBoard']},
            {label: 'All forms', to: '/forms', perms: ['RTB Forms', 'OpsBoard']},
        ],
    },
    {
        label: 'Accounting',
        items: [
            {label: 'Bills to publish', to: '/buildium/buildium-bills', perms: ['Bills', 'OpsBoard']},
            {label: 'Dext import', to: '/buildium/dext-bills-download', perms: ['Dext Bills Download', 'OpsBoard']},
            {label: 'Receipts from the app', to: '/receipts'},
            {label: 'Owner statements', to: '/buildium/statement-merge', perms: ['Merge Statements', 'OpsBoard']},
            {label: 'Bills in Buildium', to: '/buildium/bills', perms: ['Bills', 'Buildium', 'DataLink']},
            {label: 'Vendors', to: '/buildium/vendors', perms: ['Vendors', 'Buildium', 'DataLink']},
            {label: 'Chart of accounts', to: '/buildium/chart-of-accounts', perms: ['Chart of accounts', 'Buildium', 'DataLink']},
        ],
    },
    {
        label: 'Documents',
        items: [
            {label: 'Rental files', to: '/files/report-files', perms: ['Rental', 'DocHub']},
            {label: 'Strata files', to: '/buildium/association-files', perms: ['Strata', 'DocHub']},
        ],
    },
    {
        label: 'Company & settings',
        collapsed: true,
        items: [
            {label: 'Users', to: '/user/list'},
            {label: 'Roles', to: '/user/roles'},
            {label: 'Offices', to: '/offices', perms: ['Offices', 'Sterling']},
            {label: 'Email accounts', to: '/settings/mail-accounts'},
            {label: 'Email templates', to: '/settings/mail-templates'},
            {label: 'SMTP', to: '/settings/mail'},
            {label: 'Document templates', to: '/sterling/templates'},
            {label: 'Media library', to: '/material/list'},
            {label: 'Site settings', to: '/settings/general'},
            {label: 'Attachments', to: '/settings/attach'},
            {label: 'Watermark', to: '/settings/water'},
        ],
    },
];

// Bootstrap Icons (v1.10, public/lib) shown beside each entry and alone in the collapsed rail.
const ICONS = {
    'Home': 'bi-house',
    'Units & leases': 'bi-door-open',
    'Properties': 'bi-buildings',
    'Owners': 'bi-person-badge',
    'Associations': 'bi-building',
    'Rent increase notices': 'bi-graph-up-arrow',
    'Create rent increases': 'bi-plus-square',
    'Monthly run & settings': 'bi-calendar-check',
    'End of tenancy notices': 'bi-box-arrow-right',
    'Inspection reports': 'bi-clipboard-check',
    'All forms': 'bi-files',
    'Bills to publish': 'bi-receipt',
    'Dext import': 'bi-cloud-download',
    'Receipts from the app': 'bi-camera',
    'Owner statements': 'bi-file-earmark-text',
    'Bills in Buildium': 'bi-journal-text',
    'Vendors': 'bi-shop',
    'Chart of accounts': 'bi-diagram-3',
    'Rental files': 'bi-folder2-open',
    'Strata files': 'bi-folder',
    'Users': 'bi-person',
    'Roles': 'bi-shield-lock',
    'Offices': 'bi-geo-alt',
    'Email accounts': 'bi-envelope',
    'Email templates': 'bi-envelope-paper',
    'SMTP': 'bi-hdd-network',
    'Document templates': 'bi-file-earmark-richtext',
    'Media library': 'bi-images',
    'Site settings': 'bi-gear',
    'Attachments': 'bi-paperclip',
    'Watermark': 'bi-droplet',
};
NAV.forEach(group => group.items.forEach(item => {
    item.icon = ICONS[item.label] || 'bi-dot';
}));

export function isAdministrator(userInfo) {
    const perms = (userInfo && userInfo.permissions) || [];
    return !!userInfo && (userInfo.capabilities === 'administrator' || perms.includes('*'));
}

/** The groups and items this user may see; empty groups are dropped. */
export function visibleNav(userInfo, nav = NAV) {
    const admin = isAdministrator(userInfo);
    const held = new Set(((userInfo && userInfo.permissions) || []).map(p => String(p).toLowerCase()));
    const allowed = item => item.always || admin
        || (item.perms || []).some(p => held.has(p.toLowerCase()));
    return nav
        .map(group => ({...group, items: group.items.filter(allowed)}))
        .filter(group => group.items.length > 0);
}

/** Whether a menu target is the page being shown (path, plus ?type when the target has one). */
export function isActive(to, route, also = []) {
    if (also.includes(route.path)) return true;
    const [path, query = ''] = to.split('?');
    if (path !== route.path) return false;
    const want = new URLSearchParams(query).get('type');
    if (!want) return path !== '/forms' || !route.query.type;
    return route.query.type === want;
}
